/**
 * Distinguishes "the backend is unreachable" from "the backend answered and said
 * no". This matters at login: a Firebase sign-in can succeed while the pop.in
 * GraphQL endpoint is unreachable (network down, backend not running). That is NOT
 * an authorization failure — we must keep the Firebase session and show an
 * interstitial, rather than signing the user back out.
 */
export class BackendUnavailableError extends Error {
  /** The HTTP status when the backend answered with an error status; absent
   *  when it could not be reached at all. A 5xx may come after the request ran;
   *  a 4xx was refused before it ran. */
  readonly status?: number;

  constructor(message = "The pop.in backend is currently unreachable.", options?: { cause?: unknown; status?: number }) {
    super(message);
    this.name = "BackendUnavailableError";
    if (options?.cause !== undefined) this.cause = options.cause;
    if (options?.status !== undefined) this.status = options.status;
  }
}

/** Whether one GraphQL error message is the backend's mask for an unexpected
 *  failure (it also masks reads over its cost limit): "Unexpected error." */
export function isMaskedMessage(message: string): boolean {
  return /^unexpected error\.?$/i.test(message.trim());
}

/** True when the error means we couldn't reach the backend at all. */
export function isBackendUnavailable(error: unknown): error is BackendUnavailableError {
  return error instanceof BackendUnavailableError;
}

/** A readable message for a failed call: the error's own message, else `fallback`. */
export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}
